/**
 * <TutorLivePage> — le live du masteur : la pellicule, le centre, la session avec le champion (page).
 *
 * La pellicule du round 2 (Wajih, 02/11), retravaillée au round 3 pour que **le contenu au centre ait la
 * place principale** :
 *  - plus de bandeau en tête (round 4) : le masteur, le direct et sa durée, la cloche descendent dans la
 *    barre du bas ; « Tous en Concentré » et, en séance d'essai, « Prendre RDV » sont dans « Plus » ;
 *  - à gauche, la pellicule (`<TutorFilmstrip>`) : la vidéo de chaque élève et son mode, la mienne en
 *    bas ; chaque élève est une carte (sa vidéo, le fil de sa séance, l'activité en cours et « Liste · n ») ;
 *    « Tous » en tête ouvre la mosaïque ;
 *  - au centre (`<TutorLiveCenter>`) : la ligne d'objectif (l'élève, ses matières, son prochain contrôle en
 *    puce), les onglets et les gestes, puis le contenu ;
 *  - à droite, « À traiter » pour tous les élèves (`<TutorDecisionColumn>`) : les alertes, puis les propositions ;
 *    ancrée, **à largeur fixe** (Wajih, 04/11 : ni l'agrandir ni la rétrécir) ; ce qui se relit monte au
 *    centre : la relecture des propositions (`feed.review`, `<TutorReviewStage>`), le configurateur de « Plus
 *    d'options » (`feed.configurator`) et l'aperçu d'une proposition survolée (`feed.peek`) se posent sur le centre, la pellicule restant visible ; une image tenue au-dessus de la
 *    salle (`feed.drop`) fait de toute la salle la zone de dépôt ;
 *  - en bas, la barre (`<TutorLiveControlBar>`), où revient la mosaïque ;
 *  - par-dessus, le chat en volet (`<LiveChat viewer="tutor">`) et tout le reste dans `<TutorLiveOverlays>`.
 *
 * **La mosaïque** (`bar.mosaic`, ou « Tous » en tête de la pellicule) est un mode de tout l'écran
 * (`<TutorLiveMosaic>`) : tout le groupe à la place de la pellicule et du centre, sans colonne de gauche ; « À
 * traiter » reste, une main levée ne doit pas disparaître.
 *
 * **Les chevrons** (`panes`) replient la pellicule et la session pour élargir le centre (40 et 80 px, les
 * largeurs de l'ancien focus) ; repliées, les colonnes gardent leurs visages (`<TutorLiveRail>`).
 * L'ancien focus les repliait d'office au début d'une présentation sur une tablette : l'écran le
 * décide, la page le reçoit.
 *
 * « Son profil » (le visage de la ligne du centre, ou l'onglet « Profil ») ouvre la fiche de l'élève (`profile`)
 * **au centre**, dans la famille d'onglets de l'élève (live du 04/11 : sa place est là, et la colonne du champion
 * reste à côté). Au téléphone seulement, elle couvre l'écran (`<TutorLiveStudentPanel>`), son retour rendant
 * ses activités (« Ses activités »).
 *
 * Ses props suivent ses zones : `students`, `center`, `feed`, `profile`, `bar`, `overlays`.
 *
 * **Le repli est conçu, pas subi** — c'est la largeur de la page qui tranche, mesurée une fois ici :
 *  - à partir de 1 200 px, la pellicule large (232 px) et « À traiter » (380) ;
 *  - de 1 024 à 1 200, la même pellicule resserrée (208) et « À traiter » (320) ;
 *  - de 600 à 1 024 (la tablette), le fil passe en tiroir (la cloche de la barre et son compteur) ;
 *  - sous 600 (le téléphone), la pellicule devient une rangée de bulles (`<TutorStudentBubbles>`) et la
 *    liste de l'élève regardé l'onglet Activités du centre. Partager et « Présenter » y restent : l'app
 *    les offre au téléphone aujourd'hui.
 * Ancré, le fil ne fait monter aucune snackbar. La lueur n'est jamais dans le live.
 *
 * **Le centre seul** (`centerOnly`) : quand « Présenter » partage l'onglet entier (un navigateur qui ne
 * sait pas recadrer, Safari ou Firefox) ou l'écran entier (l'app), la page ne garde que le centre et la
 * barre (sans sa cloche) — ni pellicule, ni bulles, ni fil, ni fiche, ni chat, ni mosaïque : aucun prénom
 * ni aucune vidéo d'élève ne part chez les élèves (la tête du centre tait l'élève, `hideIdentity`). La barre de dessin et « Arrêter » sont dans la tête
 * du centre, ils restent ; les boîtes (`overlays`) aussi, ouvertes par le masteur.
 *
 * À la place de la salle (`notice`) : la connexion qui s'établit ou se rétablit (« Réessayer »,
 * « Annuler »), aucun élève encore là, aucun élève inscrit. Un avis passe devant la mosaïque : la page montre la
 * salle tant qu'il dure, le mode choisi revient après (`tutorLivePageModes`).
 *
 * **La zone sûre** est la sienne : la page couvre l'écran (la route du live n'a ni en-tête ni coquille),
 * alors elle se garde de l'encoche et de l'heure en haut, de la barre d'accueil en bas (iOS). La bande du
 * haut prend le fond de la page (plus de bandeau, round 4), celle du bas la couleur de la barre, qui garde
 * sa marge quand il n'y a rien à éviter (le web, Android dans son cadre où `useSafeAreaInsets()` lit zéro).
 * Toute la page en dépend, quel que soit ce qu'elle rend : connexion, centre seul, mosaïque.
 *
 * Contrôlée de bout en bout : l'élève regardé, chaque volet ouvert, chaque dialogue, chaque onglet
 * arrive en prop — chaque état s'ouvre seul dans une story. Le moteur (LiveKit) pose les pistes par
 * `renderVideo`.
 */
import React from "react"
import { Pressable, StyleSheet as RNStyleSheet, type LayoutChangeEvent, useWindowDimensions, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import { type UILiveParticipant, type UISoundMode } from "../../../ports/primary/ui/types/studentLive"
import {
    type UITutorDecisions,
    type UITutorFeedItem,
    type UITutorLiveNotice,
    type UITutorLiveRoomKind,
    type UITutorLiveStudent,
} from "../../../ports/primary/ui/types/tutorLive"
import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { EstimatedWidth, useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { CHAMPIONS, type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { TutorLiveControlBar, type TutorLiveControlBarProps } from "../../molecules/TutorLiveControlBar"
import { TutorStudentBubbles } from "../../molecules/TutorStudentBubbles"
import { type TutorHandChoice } from "../../molecules/TutorStudentTile"
import { RAIL_WIDTH, TutorLiveRail } from "../../molecules/TutorLiveRail"
import { tutorCollapsedRail, tutorPendingLabel } from "../../../ports/primary/ui/formatters/tutorSessionFormatter"
import { tutorFeedSign } from "../../molecules/TutorSessionFeedCard"
import { LiveChat, type LiveChatProps } from "../../organisms/LiveChat"
import { TutorFilmstrip } from "../../organisms/TutorFilmstrip"
import { TutorLiveMosaic } from "../../organisms/TutorLiveMosaic"
import { TutorLiveStudentPanel, type TutorLiveStudentPanelProps } from "../../organisms/TutorLiveStudentPanel"
import { TutorLiveCenter, type TutorLiveCenterProps } from "../../organisms/TutorLiveCenter"
import { decisionBadge, TutorDecisionColumn } from "../../organisms/TutorDecisionColumn"
import { type TutorSessionComposer } from "../../molecules/TutorDraftComposer"
import { type TutorSessionEntryProps } from "../../organisms/TutorSessionEntry"
import {
    TutorReviewPeek,
    TutorReviewStage,
    type TutorReviewPeekProps,
    type TutorReviewStageProps,
} from "../../organisms/TutorReviewStage"
import {
    TutorGenerationConfigurator,
    type TutorGenerationConfiguratorProps,
} from "../../organisms/TutorGenerationConfigurator"
import { Avatar } from "../../atoms/Avatar"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { TutorLiveOverlays, type TutorLiveOverlaysProps } from "../../templates/TutorLiveOverlays"
import { frenchSpacing } from "../../utils/frenchSpacing"
import { shadowStyle } from "../../utils/shadow"
import { spacing } from "../../theme/tokens/spacing"
import {
    COLUMN_TOGGLE,
    columnToggleRoom,
    FEED_ANCHORED_FROM,
    FEED_WIDTH,
    FILMSTRIP_WIDTH,
    TUTOR_LIVE_WIDE_FROM,
} from "./tutorLiveColumns"
import { tutorLivePageModes } from "./tutorLivePageModes"

/** Les élèves de la salle : la pellicule (ou les bulles), l'élève regardé, sa liste. */
export type TutorLiveStudentsZone = {
    list: UITutorLiveStudent[]
    /**
     * L'élève regardé : sa liste sous sa vidéo (au téléphone, l'onglet Activités), sa tête et sa bascule au
     * centre. `center.focus` est une activité de cet élève : l'écran la change avec lui.
     */
    selectedId?: string
    onSelect: (studentId: string) => void
    onChangeMode: (studentId: string, mode: UISoundMode) => void
    modePendingId?: string
    onOpenActivity: (studentId: string, activityId: string) => void
    /** Son fil, dans sa tuile : l'onglet « Fil » de l'élève au centre, à la ligne d'un point ou sur maintenant. */
    onOpenThread?: (studentId: string, eventId?: string) => void
    onAddActivity: (studentId: string) => void
    onOpenProfile: (studentId: string) => void
    onReorder?: (studentId: string, activityIds: string[]) => void
    onSeeMoreDone?: (studentId: string) => void
    /** Les non-lus d'un élève, sur sa vidéo : son fil du chat. */
    onOpenChat?: (studentId: string) => void
    /** Son prochain contrôle : « Contrôles » au centre. */
    onOpenAssessments?: (studentId: string) => void
    /**
     * Répondre, depuis sa carte, à la main levée d'un élève pendant un Dialogue avec un autre
     * (`list[].handChoice`) : lui parler, ou lui envoyer le champion.
     */
    onHandChoice?: (studentId: string, choice: TutorHandChoice) => void
}

/** Le centre : ce que `<TutorLiveCenter>` reçoit, hors des élèves et des pistes que la page lui donne. */
export type TutorLiveCenterZone = Omit<
    TutorLiveCenterProps,
    "students" | "selectedId" | "renderVideo" | "phone" | "gestures" | "onAddActivity" | "testID"
>

/**
 * La colonne de droite : « À traiter » pour tous les élèves, et les signaux de la séance (la cloche, la colonne
 * repliée). Son tiroir sous 1 024 px.
 */
export type TutorLiveFeedZone = {
    /** Les signaux de la séance, de tous les élèves (l'ancien panneau d'événements). */
    items: UITutorFeedItem[]
    onAction: (itemId: string, actionId: string) => void
    onDismiss: (itemId: string) => void
    open?: boolean
    onToggle: () => void
    /** « À traiter » : les alertes, puis les propositions, de tous les élèves (`tutorDecisions`). */
    decisions: UITutorDecisions
    onResult?: TutorSessionEntryProps["onResult"]
    /** Le champ : il génère pour l'élève regardé ; sans élève regardé (la mosaïque), pour tout le groupe. */
    composer?: TutorSessionComposer
    /** La carte dont un geste est en vol (sa roue). Absente (défaut) : aucune roue. */
    pending?: { itemId: string; actionId: string }
    /** Un geste d'une ligne de la génération : « Revoir », envoyer, « <champion> propose ». */
    onGeneration?: TutorSessionEntryProps["onGeneration"]
    /** Une proposition survolée dans la carte « prêtes » (web). */
    onPeek?: TutorSessionEntryProps["onPeek"]
    /** La relecture des propositions, au centre. Absente ou fermée : le centre. */
    review?: TutorReviewStageProps | null
    /** L'aperçu d'une proposition survolée, au centre. */
    peek?: TutorReviewPeekProps | null
    /** « Plus d'options » du champ : le configurateur de la génération, au centre. Absent ou fermé : le centre. */
    configurator?: TutorGenerationConfiguratorProps | null
    /** Une image tenue au-dessus de la salle (web) : pour qui elle générera (`null` : tout le groupe). */
    drop?: { firstname: string | null; avatarUrl?: string } | null
}

/** La barre du bas, hors de la fin qui tourne (dans `overlays.end`). */
export type TutorLiveBarZone = Omit<TutorLiveControlBarProps, "ending" | "presence" | "bell" | "testID">

/** Ce qui se pose par-dessus : le chat en volet, et tout ce que porte `<TutorLiveOverlays>`. */
export type TutorLiveOverlaysZone = Omit<TutorLiveOverlaysProps, "more"> & {
    /** Le chat ouvert : les props de `<LiveChat>` (sans `viewer`, toujours le masteur). */
    chat?: Omit<LiveChatProps, "viewer"> | null
    /**
     * « Plus », dont les gestes de l'ancien bandeau (round 4) : « Tous en Concentré » (sauf un individuel) et,
     * en séance d'essai, « Prendre RDV ». Ce que la page sait seule (`allFocused`) n'y est pas.
     */
    more: Omit<TutorLiveOverlaysProps["more"], "allFocused">
}

/** La fiche de l'élève ouverte par « Son profil » : ce que la page ne décide pas d'elle-même. */
export type TutorLiveProfileZone = Pick<TutorLiveStudentPanelProps, "fiche" | "onBack" | "onClose">

/** Les colonnes repliées par leurs chevrons. */
export type TutorLivePanesZone = {
    filmstripCollapsed: boolean
    feedCollapsed: boolean
    onToggleFilmstrip: () => void
    onToggleFeed: () => void
}

export type TutorLivePageProps = {
    me: UILiveParticipant
    /** Les autres tuteurs connectés à la séance : leur tuile dans la pellicule et la mosaïque. Défaut : aucun. */
    coTutors?: UILiveParticipant[]
    roomKind?: UITutorLiveRoomKind
    /** L'instant où le live a commencé (ms) : le chrono de la barre. */
    startedAt: number
    /** À la place de la salle. */
    notice?: UITutorLiveNotice
    onRetryConnection?: () => void
    onCancelConnection?: () => void
    students: TutorLiveStudentsZone
    center: TutorLiveCenterZone
    feed: TutorLiveFeedZone
    /** La fiche ouverte. Absente : le fil. */
    profile?: TutorLiveProfileZone | null
    bar: TutorLiveBarZone
    overlays: TutorLiveOverlaysZone
    /** Les chevrons de la pellicule et du fil. Absent : pas de chevron. */
    panes?: TutorLivePanesZone
    /** Le champion qui aide en séance (le monde de jeu de l'espace) : il signe la session et la pellicule. */
    helper: ChampionName
    /** La vraie piste d'un participant (`me.id` pour la mienne). */
    renderVideo?: (participantId: string) => React.ReactNode
    /**
     * Le centre seul, tant que « Présenter » partage tout l'onglet ou tout l'écran (pas de recadrage).
     * Défaut `false` : la salle entière.
     */
    centerOnly?: boolean
    testID?: string
}

const TEXTS = {
    connecting: "Connexion…",
    reconnecting: "Reconnexion…",
    mediaPendingTitle: "Autorise ta caméra et ton micro",
    // Sans dire où : la question du navigateur n'est pas au même endroit partout (une bulle près de l'adresse, une
    // fenêtre au milieu, le téléphone).
    mediaPending: frenchSpacing(
        "Ton navigateur te demande l'accès à ta caméra et à ton micro. Choisis « Autoriser » dans sa question pour entrer dans ton live."
    ),
    retry: "Réessayer",
    cancel: "Annuler",
    noStudentYet:
        "Aucun élève connecté pour le moment. Les parents sont prévenus automatiquement au bout de 5 minutes. Si tu n'as aucune nouvelle au bout de 20 minutes, tu peux clôturer le live.",
    noStudentEnrolled: "Aucun élève inscrit sur cette séance.",
    closeFeed: "Fermer « À traiter »",
    expandStudents: "Déplier les élèves",
    expandFeed: "Déplier « À traiter »",
    collapseStudents: "Replier les élèves",
    collapseFeed: "Replier « À traiter »",
    waitingCount: (count: number) => `À traiter : ${count}, déplier`,
    closeProfile: "Fermer sa fiche",
    waiting: (firstname: string, urgent: string | undefined, pending: number) =>
        [firstname, urgent, pending > 0 ? tutorPendingLabel(pending) : null].filter(Boolean).join(", "),
    backToActivities: "Ses activités",
    look: (firstname: string) => `Regarder ${firstname}`,
    dropTitle: "Lâche l'image pour générer",
    dropFor: (firstname: string | null) =>
        firstname != null ? `Pour ${firstname}, l'élève que tu regardes` : "Pour tout le groupe",
}

/** Les seuils de la salle (la largeur de la page). */
const WIDE_FROM = TUTOR_LIVE_WIDE_FROM
const PHONE_BELOW = 600
const SIDE_PANEL_MAX = 380

export function TutorLivePage(props: TutorLivePageProps) {
    const {
        me,
        coTutors,
        roomKind = "shared",
        startedAt,
        notice,
        onRetryConnection,
        onCancelConnection,
        students,
        center,
        feed,
        profile,
        bar,
        overlays,
        panes,
        helper,
        renderVideo,
        centerOnly = false,
        testID,
    } = props
    const insets = useSafeAreaInsets()
    const window = useWindowDimensions()
    const wideWidth = useWidthAtLeast(WIDE_FROM)
    const feedWidth = useWidthAtLeast(FEED_ANCHORED_FROM)
    const tabletWidth = useWidthAtLeast(PHONE_BELOW)
    const onLayout = (event: LayoutChangeEvent) => {
        wideWidth.onLayout(event)
        feedWidth.onLayout(event)
        tabletWidth.onLayout(event)
    }
    const size = wideWidth.atLeast ? "wide" : "narrow"
    const anchored = feedWidth.atLeast
    const phone = !tabletWidth.atLeast
    const around = !centerOnly
    const filmstripCollapsed = panes?.filmstripCollapsed === true
    const feedCollapsed = anchored && panes?.feedCollapsed === true
    const individual = roomKind === "individual"
    const { chat, more, ...surfaces } = overlays
    const presenting = center.present?.on === true

    // Le centre seul n'a pas de mosaïque ; un avis qui tient la salle (la connexion qui s'établit ou se rétablit, la
    // question de la caméra et du micro) non plus : son message et ses gestes sont à la place de la salle, que la
    // mosaïque recouvrirait. Un bandeau « Aucun élève » ne tient rien (`tutorLivePageModes`).
    const { held, mosaic } = tutorLivePageModes({ centerOnly, mosaicChosen: bar.mosaic?.on === true, notice })
    // La largeur du centre se déduit des colonnes posées autour : ce qui s'y ouvre (la fiche de l'élève) part de sa
    // vraie disposition, sans attendre sa mesure.
    const filmstripShown = around && !mosaic && !phone && !held && students.list.length > 0
    const leftColumn = !filmstripShown ? 0 : filmstripCollapsed ? RAIL_WIDTH.left : FILMSTRIP_WIDTH[size]
    const rightColumn = !around || !anchored ? 0 : feedCollapsed ? RAIL_WIDTH.right : FEED_WIDTH[size]
    const centerWidth =
        window.width - insets.left - insets.right - leftColumn - rightColumn - 2 * spacing[phone ? "3" : "4"]

    const focusStudentId = mosaic ? undefined : students.selectedId
    // La colonne repliée garde les visages de ceux qui attendent : un signal d'élève à pastille rouge (celle d'une
    // escalade), puis ceux dont le fil attend autre chose. Lus seulement repliée.
    const railBadge = decisionBadge(feed.decisions)
    const railItems = feedCollapsed
        ? tutorCollapsedRail({ students: students.list, items: feed.items, alerts: feed.decisions.alerts }).map(
              (row) => ({
                  id: row.student.id,
                  ...(row.signal != null && { entryKey: row.signal.id }),
                  firstname: row.student.firstname,
                  avatarUrl: row.student.avatarUrl,
                  sign:
                      row.signal != null
                          ? tutorFeedSign({ ...row.signal, escalated: true })
                          : row.urgent != null
                            ? tutorFeedSign(row.urgent)
                            : undefined,
                  label:
                      row.signal != null
                          ? `${row.student.firstname} ${row.signal.text}`
                          : TEXTS.waiting(row.student.firstname, row.urgent?.text, row.pending),
              })
          )
        : []
    // Au centre seul, pas de cloche : elle ouvrirait une session que la page ne montre pas.
    const bell =
        anchored || !around
            ? undefined
            : {
                  ...railBadge,
                  open: feed.open === true,
                  onPress: feed.onToggle,
              }

    const feedColumn = (
        <TutorDecisionColumn
            helper={helper}
            decisions={feed.decisions}
            single={students.list.length <= 1}
            selectedStudentId={focusStudentId}
            onSelectStudent={students.onSelect}
            onAction={feed.onAction}
            pending={feed.pending}
            onDismiss={feed.onDismiss}
            onResult={feed.onResult}
            onGeneration={feed.onGeneration}
            onPeek={feed.onPeek}
            peekId={feed.peek != null ? String(feed.peek.proposal.id) : null}
            composer={feed.composer}
            onClose={anchored ? undefined : feed.onToggle}
            toggleRoom={anchored && panes != null ? columnToggleRoom("right", FEED_PADDING) : undefined}
        />
    )

    // Ce qui se pose sur le centre (ou la mosaïque) : la relecture, sinon le configurateur de « Plus d'options », sinon
    // l'aperçu d'une proposition survolée. Rien en centre seul : tout l'écran part chez les élèves, et ni la relecture
    // ni les options ne sont à leur montrer.
    const centerOverlay = !around ? null : feed.review?.open === true ? (
        <TutorReviewStage {...feed.review} />
    ) : feed.configurator?.open === true ? (
        <TutorGenerationConfigurator {...feed.configurator} />
    ) : feed.peek != null && !presenting ? (
        <TutorReviewPeek {...feed.peek} />
    ) : null

    // La fiche au centre, sauf au téléphone, où elle couvre l'écran.
    const profilePanel =
        around && phone && profile != null ? (
            <TutorLiveStudentPanel {...profile} backLabel={TEXTS.backToActivities} />
        ) : null
    // Au téléphone, la fiche couvre l'écran : « + Activité » la referme. Ailleurs, elle est un onglet du centre et reste
    // ouverte : la refermer laissait le centre sur son squelette.
    const addActivity = bar.onAddActivity
    const onAddActivity =
        phone && profilePanel != null && addActivity != null
            ? () => {
                  profile?.onClose?.()
                  addActivity()
              }
            : addActivity

    // « Aucun élève… » se pose au-dessus de la salle comme de la mosaïque : il ne tient rien (`tutorLivePageModes`).
    const banner =
        notice === "no_student_yet" ? (
            <Banner message={TEXTS.noStudentYet} icon="clock" color="warning" tinted />
        ) : notice === "no_student_enrolled" ? (
            <Banner message={TEXTS.noStudentEnrolled} icon="user-friends" color="danger" tinted />
        ) : null

    const room = held ? (
        <View style={styles.connecting}>
            <Stack gap="4" align="center" style={styles.connectingText}>
                {notice === "media_pending" ? (
                    <Stack gap="2" align="center">
                        <Icon name="video" size={24} color="role" />
                        <Text size="text-lg" weight="bold" center accessibilityRole="header">
                            {TEXTS.mediaPendingTitle}
                        </Text>
                        <Text color="text-muted" center>
                            {TEXTS.mediaPending}
                        </Text>
                    </Stack>
                ) : (
                    <WaitingText>{notice === "reconnecting" ? TEXTS.reconnecting : TEXTS.connecting}</WaitingText>
                )}
                <Row gap="2">
                    {onCancelConnection != null && (
                        <Button label={TEXTS.cancel} variant="ghost" onPress={onCancelConnection} />
                    )}
                    {onRetryConnection != null && (
                        <Button label={TEXTS.retry} variant="outline" onPress={onRetryConnection} />
                    )}
                </Row>
            </Stack>
        </View>
    ) : (
        <Stack gap="3" style={styles.flex}>
            {banner}
            <TutorLiveCenter
                {...center}
                profileTab={!phone && around}
                profile={!phone && around ? profile?.fiche : undefined}
                students={students.list}
                selectedId={students.selectedId}
                renderVideo={renderVideo}
                phone={phone}
                gestures={students}
                // « + Activité » est dans la barre flottante du centre ; le téléphone, qui n'en a pas, le garde en bas.
                onAddActivity={phone ? undefined : onAddActivity}
            />
        </Stack>
    )

    return (
        <View style={styles.root(insets)} onLayout={onLayout} testID={testID}>
            {/* La fiche ouverte couvre le corps : ses bulles touchées changeaient l'élève sous une fiche restée la même. */}
            {around && phone && !held && !mosaic && profilePanel == null && students.list.length > 0 && (
                <View style={styles.bubbles}>
                    <TutorStudentBubbles
                        students={students.list}
                        selectedId={students.selectedId}
                        onSelect={students.onSelect}
                        me={me}
                        presenting={presenting}
                        renderVideo={renderVideo}
                    />
                </View>
            )}
            <View style={styles.body}>
                {mosaic && (
                    <Stack gap="3" style={[styles.center(phone), styles.centerStage]}>
                        {banner}
                        {centerOverlay}
                        <TutorLiveMosaic
                            students={students.list}
                            me={me}
                            coTutors={coTutors}
                            onSelect={students.onSelect}
                            onOpenActivity={students.onOpenActivity}
                            onOpenThread={students.onOpenThread}
                            onChangeMode={students.onChangeMode}
                            modePendingId={students.modePendingId}
                            renderVideo={renderVideo}
                        />
                    </Stack>
                )}
                {filmstripShown && filmstripCollapsed && (
                    <View style={styles.rail(RAIL_WIDTH.left, "left")}>
                        <TutorLiveRail
                            items={students.list.map((student) => ({
                                id: student.id,
                                firstname: student.firstname,
                                avatarUrl: student.avatarUrl,
                                selected: student.id === students.selectedId,
                                label: TEXTS.look(student.firstname),
                            }))}
                            onPressItem={students.onSelect}
                            onExpand={panes!.onToggleFilmstrip}
                        />
                        <ColumnToggle
                            side="left"
                            collapsed
                            onPress={panes!.onToggleFilmstrip}
                            label={TEXTS.expandStudents}
                        />
                    </View>
                )}
                {filmstripShown && !filmstripCollapsed && (
                    <View style={styles.filmstrip(FILMSTRIP_WIDTH[size])}>
                        <TutorFilmstrip
                            students={students.list}
                            selectedId={students.selectedId}
                            onSelect={students.onSelect}
                            onChangeMode={students.onChangeMode}
                            modePendingId={students.modePendingId}
                            openActivityId={center.focus?.activityId}
                            onOpenActivity={students.onOpenActivity}
                            onOpenThread={students.onOpenThread}
                            onReorder={students.onReorder}
                            onSeeMoreDone={students.onSeeMoreDone}
                            onOpenChat={students.onOpenChat}
                            helperName={CHAMPIONS[helper].label}
                            onHandChoice={students.onHandChoice}
                            toggleRoom={panes != null ? columnToggleRoom("left", FILMSTRIP_PADDING_LEFT) : undefined}
                            onShowAll={bar.mosaic != null && !bar.mosaic.on ? bar.mosaic.onToggle : undefined}
                            me={me}
                            coTutors={coTutors}
                            meState={presenting ? "presenting" : bar.screenShare?.on === true ? "sharing" : undefined}
                            renderVideo={renderVideo}
                        />
                        {panes != null && (
                            <ColumnToggle
                                side="left"
                                collapsed={false}
                                onPress={panes.onToggleFilmstrip}
                                label={TEXTS.collapseStudents}
                            />
                        )}
                    </View>
                )}
                {!mosaic && (
                    <View style={[styles.center(phone), styles.centerStage]}>
                        <EstimatedWidth width={centerWidth}>
                            {room}
                            {!held && centerOverlay}
                        </EstimatedWidth>
                    </View>
                )}
                {around && anchored && feedCollapsed && (
                    <View style={styles.rail(RAIL_WIDTH.right, "right")}>
                        <TutorLiveRail
                            items={railItems}
                            count={{
                                value: railBadge.count,
                                urgent: railBadge.urgent,
                                label: TEXTS.waitingCount(railBadge.count),
                            }}
                            onPressItem={(studentId) => {
                                students.onSelect(studentId)
                                panes!.onToggleFeed()
                            }}
                            onExpand={panes!.onToggleFeed}
                        />
                        <ColumnToggle side="right" collapsed onPress={panes!.onToggleFeed} label={TEXTS.expandFeed} />
                    </View>
                )}
                {around && anchored && !feedCollapsed && (
                    <View style={styles.feed(FEED_WIDTH[size])}>
                        {feedColumn}
                        {panes != null && (
                            <ColumnToggle
                                side="right"
                                collapsed={false}
                                onPress={panes.onToggleFeed}
                                label={TEXTS.collapseFeed}
                            />
                        )}
                    </View>
                )}
                {profilePanel != null && (
                    <>
                        <Pressable
                            style={styles.scrim}
                            onPress={profile?.onClose}
                            accessibilityLabel={TEXTS.closeProfile}
                        />
                        <View style={styles.profileDrawer}>{profilePanel}</View>
                    </>
                )}

                {around && !anchored && profilePanel == null && feed.open === true && (
                    <>
                        <Pressable style={styles.scrim} onPress={feed.onToggle} accessibilityLabel={TEXTS.closeFeed} />
                        <View style={styles.drawer(phone)}>{feedColumn}</View>
                    </>
                )}
                {around && chat != null && (
                    <View style={[styles.drawer(phone), styles.chat]}>
                        <LiveChat {...chat} viewer="tutor" />
                    </View>
                )}
                {around && feed.drop != null && (
                    <View style={styles.drop} pointerEvents="none">
                        <Stack gap="4" align="center" style={styles.dropCard}>
                            <IconBubble
                                iconName="file-image"
                                size={64}
                                iconSize={28}
                                color="role-fill"
                                iconColor="text-on-role"
                            />
                            <Text size="text-2xl" weight="bold" color="text-strong" center>
                                {TEXTS.dropTitle}
                            </Text>
                            <Row gap="2" align="center" style={styles.dropTarget}>
                                {feed.drop.firstname != null && (
                                    <Avatar uri={feed.drop.avatarUrl} name={feed.drop.firstname} size={28} />
                                )}
                                <Text weight="semibold" color="role-strong">
                                    {TEXTS.dropFor(feed.drop.firstname)}
                                </Text>
                            </Row>
                        </Stack>
                    </View>
                )}
            </View>
            <View style={styles.bar(insets.bottom)}>
                <TutorLiveControlBar
                    {...bar}
                    onAddActivity={phone ? onAddActivity : undefined}
                    presence={{ tutor: me, studentCount: students.list.length, startedAt }}
                    bell={bell}
                    ending={overlays.end.finalizing === true || overlays.end.leaving === true}
                />
            </View>

            <TutorLiveOverlays
                {...surfaces}
                more={{
                    ...more,
                    onAllFocused: individual ? undefined : more.onAllFocused,
                    onBookCall: roomKind === "trial" ? more.onBookCall : undefined,
                    allFocused: students.list.every(
                        (student) => student.presence !== "here" || student.soundMode === "focused"
                    ),
                }}
            />
        </View>
    )
}

/** Le retrait gauche de la pellicule et celui de « À traiter » : la place que leur en-tête garde au chevron en dépend. */
const FILMSTRIP_PADDING_LEFT = spacing["4"]
const FEED_PADDING = spacing["3"]

/**
 * Le chevron qui replie ou déplie une colonne, posé par la page dans le coin haut extérieur de la colonne, à la même
 * place repliée et dépliée (`COLUMN_TOGGLE`) : le masteur le retouche sans chercher.
 */
function ColumnToggle(props: { side: "left" | "right"; collapsed: boolean; onPress: () => void; label: string }) {
    const { side, collapsed, onPress, label } = props
    return (
        <View style={styles.toggle(side)}>
            <IconButton
                iconName={`panel-${side}-${collapsed ? "open" : "close"}`}
                appearance="transparent"
                iconColor="text-muted"
                size={COLUMN_TOGGLE.size}
                iconSize={COLUMN_TOGGLE.iconSize}
                onPress={onPress}
                accessibilityLabel={label}
            />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** La zone sûre : l'encoche en haut et sur les côtés, sur le fond de la page (plus de bandeau, round 4). */
    root: (insets: { top: number; left: number; right: number }) => ({
        flex: 1,
        minHeight: 0,
        paddingTop: insets.top,
        paddingLeft: insets.left,
        paddingRight: insets.right,
        backgroundColor: theme.colors.surface,
    }),
    body: { flex: 1, minHeight: 0, flexDirection: "row" },
    bubbles: { paddingHorizontal: theme.spacing["3"], paddingTop: theme.spacing["2"] },
    filmstrip: (width: number) => ({
        width,
        minHeight: 0,
        paddingVertical: theme.spacing["3"],
        paddingLeft: FILMSTRIP_PADDING_LEFT,
        paddingRight: theme.spacing["3"],
        borderRightWidth: 1,
        borderRightColor: theme.colors["border-subtle"],
    }),
    center: (phone: boolean) => ({
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        paddingHorizontal: theme.spacing[phone ? "3" : "4"],
        paddingVertical: theme.spacing["3"],
    }),
    /** Une colonne repliée : sa largeur de l'ancien focus, son filet du côté du centre ; ses visages sous le chevron. */
    rail: (width: number, side: "left" | "right") => ({
        width,
        minHeight: 0,
        paddingTop: COLUMN_TOGGLE.top[side] + COLUMN_TOGGLE.size + theme.spacing["2"],
        paddingBottom: theme.spacing["3"],
        ...(side === "left"
            ? { borderRightWidth: 1, borderRightColor: theme.colors["border-subtle"] }
            : { borderLeftWidth: 1, borderLeftColor: theme.colors["border-subtle"] }),
    }),
    feed: (width: number) => ({
        width,
        minHeight: 0,
        padding: FEED_PADDING,
        borderLeftWidth: 1,
        borderLeftColor: theme.colors["border-subtle"],
    }),
    /** La fiche, au téléphone : tout l'écran. */
    profileDrawer: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        backgroundColor: theme.colors.surface,
        ...shadowStyle(theme, "floating"),
    },
    /** Le chevron d'une colonne, au même point repliée et dépliée. */
    toggle: (side: "left" | "right") => ({
        position: "absolute" as const,
        top: COLUMN_TOGGLE.top[side],
        [side]: COLUMN_TOGGLE.inset[side],
        zIndex: 1,
    }),
    flex: { flex: 1, minHeight: 0 },
    connecting: { flex: 1, alignItems: "center" as const, justifyContent: "center" as const },
    connectingText: { maxWidth: 420, paddingHorizontal: theme.spacing["4"] },
    /** Le centre porte ce qui se pose sur lui : la relecture, l'aperçu. */
    centerStage: { position: "relative" as const },
    /** Une image tenue au-dessus de la salle : toute la salle devient la zone de dépôt. */
    drop: {
        ...RNStyleSheet.absoluteFillObject,
        padding: theme.spacing["3"],
        backgroundColor: theme.colors.scrim,
        zIndex: 10,
    },
    dropCard: {
        flex: 1,
        justifyContent: "center" as const,
        borderRadius: theme.radius.xl,
        borderWidth: 3,
        borderStyle: "dashed" as const,
        borderColor: theme.colors.role,
        backgroundColor: theme.colors.surface,
    },
    dropTarget: {
        paddingLeft: theme.spacing["1"],
        paddingRight: theme.spacing["4"],
        paddingVertical: theme.spacing["1"],
        borderRadius: theme.radius.full,
        borderWidth: 2,
        borderColor: theme.colors.role,
    },
    scrim: { ...RNStyleSheet.absoluteFillObject, backgroundColor: theme.colors.scrim },
    /** Le tiroir : la largeur d'une colonne ; au téléphone, tout l'écran, sans bande de voile à gauche. */
    drawer: (phone: boolean) => ({
        position: "absolute" as const,
        top: 0,
        right: 0,
        bottom: 0,
        width: "100%" as const,
        maxWidth: phone ? undefined : SIDE_PANEL_MAX,
        padding: theme.spacing["3"],
        backgroundColor: theme.colors.surface,
        ...shadowStyle(theme, "floating"),
    }),
    chat: { padding: 0, backgroundColor: theme.colors["surface-raised"] },
    /** La barre d'accueil (iOS) passe dans la barre, qui garde au moins sa marge d'ici. */
    bar: (bottomInset: number) => ({
        paddingBottom: Math.max(bottomInset, theme.spacing["2"]),
        backgroundColor: theme.colors["surface-raised"],
        borderTopWidth: 1,
        borderTopColor: theme.colors["border-subtle"],
    }),
}))
